import React, { useState, useEffect } from 'react';
import { useThemeMode } from '../../ThemeContext';
import { dataAPI } from '../../helpers/api_service';
import { getProfileCompanyPicUrl } from '../../helpers/url_helper';
import { downloadFile, viewFile, formatDate } from '../../helpers/UtilsFunctions';
import { format } from 'date-fns';
import AppIconBlue from '../../assets/AppIconBlue.svg';
import '../../styles/loading-animations.css';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Person as PersonIcon,
  Flag as FlagIcon,
  Assignment as AssignmentIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import CalendarIcon from '../../assets/Calendar.svg';
import UserIcon from '../../assets/user.svg';
import Users2Icon from '../../assets/Users_2.svg';
import EyeOpenIcon from '../../assets/eye-open.svg';
import DownloadIcon from '../../assets/Download.svg';
import DescriptionIcon from '@mui/icons-material/Description';
import CategoryIcon from '@mui/icons-material/Category';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import icPriority from '../../assets/ic_priority.svg';

// Helper function to handle profile picture URL (same as DashboardLayout)
const getProfilePicUrl = (profilePic) => {
  if (!profilePic) return '';
  
  // Check if URL already starts with http (any protocol)
  if (profilePic.toLowerCase().startsWith('http://') || profilePic.toLowerCase().startsWith('https://')) {
    return profilePic; // Return as is if it's already a full URL
  } else {
    return getProfileCompanyPicUrl(profilePic); // Append profile/company pic URL prefix if it's a relative path
  }
};

const TaskDetailSidePanel = ({ isOpen, onClose, taskId }) => {
  const { mode } = useThemeMode();
  const [task, setTask] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showFormLoading, setShowFormLoading] = useState(false);
  const [error, setError] = useState('');
  const [downloadingFiles, setDownloadingFiles] = useState(new Set());

  useEffect(() => {
    if (isOpen && taskId) {
      fetchTaskDetails();
    }
  }, [isOpen, taskId]);

  const fetchTaskDetails = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await dataAPI.getTaskDetail(taskId);
      
      if (response.status === 'success') {
        setTask(response.data);
      } else {
        setError('Failed to load task details');
      }
    } catch (error) {
      console.error('Error fetching task details:', error);
      setError('Failed to load task details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setTask(null);
    setError('');
    setShowFormLoading(false);
    onClose();
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return <PictureAsPdfIcon style={{ fontSize: '24px', color: '#f44336' }} />;
      case 'doc':
      case 'docx':
        return <InsertDriveFileIcon style={{ fontSize: '24px', color: '#2196f3' }} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
        return <ImageIcon style={{ fontSize: '24px', color: '#4caf50' }} />;
      default:
        return <InsertDriveFileIcon style={{ fontSize: '24px', color: '#666' }} />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return '#f44336';
      case 'medium':
        return '#ff9800';
      case 'low':
        return '#4caf50';
      default:
        return '#666666';
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <style>
        {`
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}
      </style>
      
      {/* Backdrop */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          backdropFilter: 'blur(2px)'
        }}
        onClick={handleClose}
      />
      
      {/* Side Panel */}
      <div 
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          width: '420px',
          height: 'calc(100vh - 40px)',
          backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
          boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.3)',
          zIndex: 1001,
          overflowY: 'auto',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease-in-out',
          borderRadius: '12px',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
        className="hide-scrollbar"
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 20px 8px 20px',
          borderBottom: `1px solid ${mode === 'dark' ? '#333' : '#e0e0e0'}`
        }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: mode === 'dark' ? '#FFFFFF' : '#222222',
            margin: 0,
            fontFamily: "'Urbanist', Arial, sans-serif"
          }}>
            Task Details
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '4px',
              color: mode === 'dark' ? '#FFFFFF' : '#666666',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CloseIcon style={{ fontSize: '20px' }} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '16px 20px', position: 'relative' }}>
          {/* Loading Overlay - Shows when loading task details */}
          {isLoading && (
            <div style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              width: '420px',
              height: 'calc(100vh - 40px)',
              zIndex: 1003,
              backgroundColor: mode === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(4px)',
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px'
            }}>
              {/* Fill Animation Loading Indicator */}
              <div className="fill-animation-container">
                {/* Background container */}
                <div className="fill-animation-background" />
                
                {/* Progress fill animation */}
                <div className="fill-animation-progress" />
                
                {/* AppIconBlue with pulse animation */}
                <img 
                  src={AppIconBlue} 
                  alt="Loading" 
                  className={`fill-animation-icon pulse-animation-icon`}
                />
              </div>
            </div>
          )}

          {/* Form Loading Overlay - Only shows during form submission */}
          {showFormLoading && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1003,
              backgroundColor: mode === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(4px)',
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px'
            }}>
              {/* Fill Animation Loading Indicator */}
              <div className="fill-animation-container">
                {/* Background container */}
                <div className="fill-animation-background" />
                
                {/* Progress fill animation */}
                <div className="fill-animation-progress" />
                
                {/* AppIconBlue with pulse animation */}
                <img 
                  src={AppIconBlue} 
                  alt="Loading" 
                  className={`fill-animation-icon pulse-animation-icon`}
                />
              </div>
            </div>
          )}

          {error && (
            <div style={{
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#ffebee',
              border: '1px solid #f44336',
              borderRadius: '6px',
              color: '#d32f2f',
              fontSize: '14px',
              fontFamily: "'Urbanist', Arial, sans-serif"
            }}>
              {error}
            </div>
          )}

          {task && (
            <>
              {/* Task Title */}
              <div style={{ marginBottom: '20px' }}>
                <h1 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#26C3C0',
                  margin: '0 0 8px 0',
                  fontFamily: "'Urbanist', Arial, sans-serif",
                  lineHeight: '1.2'
                }}>
                  {task.title}
                </h1>
              </div>

              {/* Task Details */}
              <div style={{
                backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '20px',
                border: `1px solid ${mode === 'dark' ? '#444' : '#e0e0e0'}`
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px'
                }}>
                                     {/* Assigned By */}
                   <div style={{
                     display: 'flex',
                     alignItems: 'center',
                     gap: '8px'
                   }}>
                     <div style={{
                       display: 'flex',
                       alignItems: 'center',
                       gap: '8px',
                       minWidth: '120px'
                     }}>
                       <img 
                         src={UserIcon} 
                         alt="Assigned By" 
                         style={{ 
                           width: '18px', 
                           height: '18px',
                           filter: mode === 'dark' ? 'brightness(0) invert(1)' : 'brightness(0) saturate(100%) invert(40%)',
                           opacity: 0.7
                         }} 
                       />
                       <span style={{
                         fontSize: '14px',
                         color: mode === 'dark' ? '#999' : '#666',
                         fontFamily: "'Urbanist', Arial, sans-serif",
                         fontWeight: '500'
                       }}>
                         Assigned By
                       </span>
                     </div>
                     <div style={{
                       display: 'flex',
                       alignItems: 'center'
                     }}>
                    <span style={{
                         padding: '8px 8px',
                      backgroundColor: 'transparent',
                         color: mode === 'dark' ? '#FFFFFF' : '#222222',
                         borderRadius: '8px',
                         fontSize: '14px',
                      fontWeight: '500',
                      fontFamily: "'Urbanist', Arial, sans-serif",
                         border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`,
                      display: 'flex',
                      alignItems: 'center',
                         gap: '8px'
                       }}>
                         {task.created_by_profile_pic ? (
                           <img
                             src={getProfilePicUrl(task.created_by_profile_pic)}
                             alt="Profile"
                             style={{
                               width: '20px',
                               height: '20px',
                               borderRadius: '50%',
                               objectFit: 'cover'
                             }}
                             onError={(e) => {
                               // Replace the image with a fallback avatar when it fails to load
                               const fallbackDiv = document.createElement('div');
                               fallbackDiv.style.cssText = `
                                 width: 20px;
                                 height: 20px;
                                 border-radius: 50%;
                                 background-color: #26C3C0;
                                 display: flex;
                                 align-items: center;
                                 justify-content: center;
                                 color: #FFFFFF;
                                 font-size: 12px;
                                 font-weight: 600;
                               `;
                               fallbackDiv.textContent = task.created_by_username ? task.created_by_username.charAt(0).toUpperCase() : 'U';
                               e.target.parentNode.replaceChild(fallbackDiv, e.target);
                             }}
                           />
                         ) : (
                      <div style={{
                             width: '20px',
                             height: '20px',
                        borderRadius: '50%',
                        backgroundColor: '#26C3C0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#FFFFFF',
                             fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {task.created_by_username ? task.created_by_username.charAt(0).toUpperCase() : 'U'}
                      </div>
                         )}
                         {task?.created_by_user_details?.full_name || 'Unknown'}
                    </span>
                     </div>
                  </div>

                                     {/* Assigned To */}
                   <div style={{
                     display: 'flex',
                     alignItems: 'center',
                     gap: '8px'
                   }}>
                     <div style={{
                       display: 'flex',
                       alignItems: 'center',
                       gap: '8px',
                       minWidth: '120px'
                     }}>
                       <img 
                         src={Users2Icon} 
                         alt="Assigned To" 
                         style={{ 
                           width: '18px', 
                           height: '18px',
                           filter: mode === 'dark' ? 'brightness(0) invert(1)' : 'brightness(0) saturate(100%) invert(40%)',
                           opacity: 0.7
                         }} 
                       />
                       <span style={{
                         fontSize: '14px',
                         color: mode === 'dark' ? '#999' : '#666',
                         fontFamily: "'Urbanist', Arial, sans-serif",
                         fontWeight: '500'
                       }}>
                         Assigned to
                       </span>
                     </div>
                     <div style={{
                       display: 'flex',
                       alignItems: 'center'
                     }}>
                    <span style={{
                         padding: '8px 8px',
                      backgroundColor: 'transparent',
                         color: mode === 'dark' ? '#FFFFFF' : '#222222',
                         borderRadius: '8px',
                         fontSize: '14px',
                      fontWeight: '500',
                      fontFamily: "'Urbanist', Arial, sans-serif",
                         border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`,
                      display: 'flex',
                      alignItems: 'center',
                         gap: '8px'
                    }}>
                      {task.assigned_user_details?.profile_pic ? (
                        <img
                          src={getProfilePicUrl(task.assigned_user_details.profile_pic)}
                          alt="Profile"
                          style={{
                               width: '20px',
                               height: '20px',
                            borderRadius: '50%',
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            // Replace the image with a fallback avatar when it fails to load
                            const fallbackDiv = document.createElement('div');
                            fallbackDiv.style.cssText = `
                                 width: 20px;
                                 height: 20px;
                              border-radius: 50%;
                              background-color: #26C3C0;
                              display: flex;
                              align-items: center;
                              justify-content: center;
                              color: #FFFFFF;
                                 font-size: 12px;
                              font-weight: 600;
                            `;
                            fallbackDiv.textContent = task.assigned_user_details?.full_name ? task.assigned_user_details.full_name.charAt(0).toUpperCase() : 'U';
                            e.target.parentNode.replaceChild(fallbackDiv, e.target);
                          }}
                        />
                      ) : (
                        <div style={{
                             width: '20px',
                             height: '20px',
                          borderRadius: '50%',
                          backgroundColor: '#26C3C0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#FFFFFF',
                             fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          {task.assigned_user_details?.full_name ? task.assigned_user_details.full_name.charAt(0).toUpperCase() : 'U'}
                        </div>
                      )}
                      {task.assigned_user_details?.full_name || 'Unassigned'}
                    </span>
                     </div>
                  </div>

                  {/* Timeline */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      minWidth: '100px'
                    }}>
                      <img
                        src={CalendarIcon}
                        alt="Calendar"
                        style={{
                          width: '18px',
                          height: '18px',
                          filter: mode === 'dark' ? 'brightness(0) invert(1)' : 'brightness(0) saturate(100%) invert(40%)',
                          opacity: 0.7
                        }}
                      />
                      <span style={{
                        fontSize: '13px',
                        color: mode === 'dark' ? '#999' : '#666',
                        fontFamily: "'Urbanist', Arial, sans-serif",
                        fontWeight: '500'
                      }}>
                        Timeline
                      </span>
                    </div>
                    <span style={{
                      padding: '6px 12px',
                      backgroundColor: 'transparent',
                      color: mode === 'dark' ? '#FFFFFF' : '#222222',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '500',
                      fontFamily: "'Urbanist', Arial, sans-serif",
                      border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`
                    }}>
                      {task.start_date && task.due_date ? 
                        `${formatDate(task.start_date) || 'Not set'} - ${formatDate(task.due_date) || 'Not set'}` : 
                        task.due_date ? 
                          `Not set - ${formatDate(task.due_date) || 'Not set'}` :
                          task.start_date ?
                            `${formatDate(task.start_date) || 'Not set'} - Not set` :
                            'Timeline not set'
                      }
                    </span>
                  </div>

                  {/* Priority and Status */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      minWidth: '100px'
                    }}>
                      <CategoryIcon style={{ fontSize: '16px', color: mode === 'dark' ? '#999' : '#666' }} />
                      <span style={{
                        fontSize: '13px',
                        color: mode === 'dark' ? '#999' : '#666',
                        fontFamily: "'Urbanist', Arial, sans-serif",
                        fontWeight: '500'
                      }}>
                        Tags
                      </span>
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: '8px'
                    }}>
                      <span style={{
                        padding: '6px 12px',
                        backgroundColor: getPriorityColor(task.priority),
                        color: '#FFFFFF',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        fontFamily: "'Urbanist', Arial, sans-serif",
                        textTransform: 'capitalize'
                      }}>
                        {task.priority || 'Medium'}
                      </span>
                      <span style={{
                        padding: '6px 12px',
                        backgroundColor: '#666666',
                        color: '#FFFFFF',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        fontFamily: "'Urbanist', Arial, sans-serif",
                        textTransform: 'capitalize'
                      }}>
                        {task.status || 'To Do'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div style={{
                backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '20px',
                border: `1px solid ${mode === 'dark' ? '#444' : '#e0e0e0'}`
              }}>
                <style>
                  {`
                    /* Hide scrollbar but keep scrolling functionality */
                    .description-scroll-sidepanel::-webkit-scrollbar {
                      width: 0;
                      display: none;
                    }
                    
                    .description-scroll-sidepanel::-webkit-scrollbar-track {
                      display: none;
                    }
                    
                    .description-scroll-sidepanel::-webkit-scrollbar-thumb {
                      display: none;
                    }
                    
                    /* Firefox scrollbar - hide completely */
                    .description-scroll-sidepanel {
                      scrollbar-width: none;
                      -ms-overflow-style: none;
                    }
                  `}
                </style>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '16px'
                }}>
                  <DescriptionIcon style={{ fontSize: '16px', color: '#26C3C0' }} />
                  <span style={{
                    fontSize: '15px',
                    fontWeight: '600',
                    color: mode === 'dark' ? '#FFFFFF' : '#222222',
                    fontFamily: "'Urbanist', Arial, sans-serif"
                  }}>
                    Description
                  </span>
                </div>
                                 <p 
                   className="description-scroll-sidepanel"
                   style={{
                   fontSize: '13px',
                     lineHeight: '1.6',
                   color: mode === 'dark' ? '#CCCCCC' : '#666666',
                   margin: '0 0 16px 0',
                     fontFamily: "'Urbanist', Arial, sans-serif",
                     maxHeight: '120px', // 5 lines * 24px line height
                     overflow: 'auto',
                     whiteSpace: 'pre-wrap',
                     wordWrap: 'break-word',
                     textAlign: 'justify'
                   }}
                   dangerouslySetInnerHTML={{ 
                     __html: task.description || 'No description available' 
                   }}
                 />
                
                {/* Subtasks */}
                {task.subtasks && task.subtasks.length > 0 && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px'
                  }}>
                    {task.subtasks.map((subtask, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <div style={{
                          width: '14px',
                          height: '14px',
                          border: `2px solid ${mode === 'dark' ? '#666' : '#999'}`,
                          borderRadius: '3px',
                          backgroundColor: 'transparent'
                        }} />
                        <span style={{
                          fontSize: '13px',
                          color: mode === 'dark' ? '#CCCCCC' : '#666666',
                          fontFamily: "'Urbanist', Arial, sans-serif"
                        }}>
                          {subtask.title || `Subtask ${index + 1}`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Attachments */}
              <div style={{
                backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '20px',
                border: `1px solid ${mode === 'dark' ? '#444' : '#e0e0e0'}`
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <AttachFileIcon style={{ fontSize: '16px', color: '#26C3C0' }} />
                    <span style={{
                      fontSize: '15px',
                      fontWeight: '600',
                      color: mode === 'dark' ? '#FFFFFF' : '#222222',
                      fontFamily: "'Urbanist', Arial, sans-serif"
                    }}>
                      Attachments
                    </span>
                  </div>
                </div>

                {/* Attachments List */}
                {task.attachments && task.attachments.length > 0 ? (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}>
                    {task.attachments.map((attachment, index) => (
                      <div key={attachment.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px',
                        backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
                        borderRadius: '8px',
                        border: `1px solid ${mode === 'dark' ? '#444' : '#e0e0e0'}`
                      }}>
                        {getFileIcon(attachment.file_name)}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: '500',
                            color: mode === 'dark' ? '#FFFFFF' : '#222222',
                            marginBottom: '3px',
                            fontFamily: "'Urbanist', Arial, sans-serif",
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {attachment.file_name}
                          </div>
                          <div style={{
                            fontSize: '11px',
                            color: mode === 'dark' ? '#999' : '#666',
                            fontFamily: "'Urbanist', Arial, sans-serif"
                          }}>
                            {new Date(attachment.uploaded_at).toLocaleString('en-US', {
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true,
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </div>
                        </div>
                        <div style={{
                          display: 'flex',
                          gap: '6px'
                        }}>
                          <button 
                            style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px',
                            borderRadius: '4px',
                            transition: 'background-color 0.2s ease'
                            }}
                            onClick={() => {
                              if (attachment.file_url) {
                                viewFile(attachment.file_url, attachment.file_name);
                              }
                            }}
                          >
                            <img
                              src={EyeOpenIcon}
                              alt="View"
                              style={{
                                width: '18px',
                                height: '18px',
                                filter: 'brightness(0) saturate(100%) invert(70%) sepia(50%) saturate(500%) hue-rotate(160deg) brightness(90%) contrast(120%)'
                              }}
                            />
                          </button>
                          <button 
                            style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px',
                            borderRadius: '4px',
                            color: mode === 'dark' ? '#FFFFFF' : '#666',
                              transition: 'background-color 0.2s ease',
                              opacity: downloadingFiles.has(attachment.id) ? 0.5 : 1
                            }}
                            disabled={downloadingFiles.has(attachment.id)}
                            onClick={async () => {
                              if (!attachment.file_url || !attachment.file_name) {
                                return;
                              }
                              
                              const fileId = attachment.id;
                              setDownloadingFiles(prev => new Set(prev).add(fileId));
                              try {
                                // Create a custom download function that cleans the final URL
                                const downloadWithCleanUrl = async (filePath, fileName) => {
                                  const { FILE_DOWNLOAD_PREFIX } = require('../../helpers/url_helper');
                                  
                                  // Construct the full URL
                                  let downloadUrl;
                                  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
                                    downloadUrl = filePath;
                                  } else {
                                    downloadUrl = `${FILE_DOWNLOAD_PREFIX}${filePath}`;
                                  }
                                  
                                  // Clean double slashes while preserving protocol slashes
                                  downloadUrl = downloadUrl.replace(/([^:])\/\/+/g, '$1/');
                                  
                                  // Use the original downloadFile logic with the cleaned URL
                                  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
                                  
                                  const response = await fetch(downloadUrl, {
                                    method: 'GET',
                                    headers: token ? {
                                      'Authorization': `Bearer ${token}`,
                                      'Content-Type': 'application/octet-stream'
                                    } : {
                                      'Content-Type': 'application/octet-stream'
                                    }
                                  });

                                  if (!response.ok) {
                                    throw new Error(`HTTP error! status: ${response.status}`);
                                  }

                                  const blob = await response.blob();
                                  const blobUrl = window.URL.createObjectURL(blob);
                                  
                                  const link = document.createElement('a');
                                  link.href = blobUrl;
                                  link.download = fileName || 'document';
                                  link.style.display = 'none';
                                  
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                  
                                  window.URL.revokeObjectURL(blobUrl);
                                };
                                
                                await downloadWithCleanUrl(attachment.file_url, attachment.file_name);
                              } catch (error) {
                                console.error('Download failed:', error);
                              } finally {
                                setDownloadingFiles(prev => {
                                  const newSet = new Set(prev);
                                  newSet.delete(fileId);
                                  return newSet;
                                });
                              }
                            }}
                          >
                            <img
                              src={DownloadIcon}
                              alt="Download"
                              style={{
                                width: '18px',
                                height: '18px',
                                filter: 'brightness(0) saturate(100%) invert(70%) sepia(50%) saturate(500%) hue-rotate(160deg) brightness(90%) contrast(120%)'
                              }}
                            />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '20px',
                    color: mode === 'dark' ? '#999' : '#666',
                    fontFamily: "'Urbanist', Arial, sans-serif",
                    fontSize: '13px'
                  }}>
                    No attachments found
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default TaskDetailSidePanel;
